import { PageErrorBoundary } from '@citizen-walk/shared-ui';
import {
  BrowserRouter,
  Navigate,
  Outlet,
  Route,
  Routes,
  useLocation,
  useParams,
} from 'react-router-dom';
import { AppHome } from './AppHome';
import { AnalysisPage } from './features/analysis';
import { RecoveryAutoPage, RecoveryIssuePage, RecoveryRecoverPage } from './features/auth';
import {
  DistrictHomePage,
  DistrictProvider,
  NonPublicScreen,
  useDistrictAccessGuard,
  useDistrictState,
} from './features/district';
import { GuidePage } from './features/help';
import { UpdateBanner } from './features/shell';
import {
  AccountLoginPage,
  EntryPage,
  OnboardingPage,
  RequireOnboarding,
} from './features/onboarding';
import { captureException } from './lib/sentry';
import { MapDownloadPage, MapPage } from './features/map';
// セッションごとの参加 QR (2026-09-04) の受け口。
import { SessionJoinPage } from './features/sessions';
// 短縮アドレス (docs/26) の受け口。イベント会場で PC のアドレスバーに手入力するための短い入口。
import { ShortLinkPage } from './features/shortlink';

/**
 * Phase 3 sprint 2 stage 2 — React Router 導入。
 *
 * Route 設計 (旧 `App.tsx` 踏襲、Sprint BG-DataSettings 9 で `/` を `DistrictHomePage` に置換):
 *   - `/`                  : 参加地区一覧ホーム (旧 v1 `DistrictHomePage`、GAP U-S1 移植)
 *   - `/guide`             : 使い方ガイド (地区非依存)
 *   - `/s/:code`           : 短縮アドレス (docs/26) の受け口。`/m/:urlToken` へ置換遷移する中継点
 *   - `/m/:urlToken`                   : DistrictProvider 配下の AppHome
 *   - `/m/:urlToken/map`               : DistrictProvider 配下の MapPage (Phase 5.3 minimum)
 *   - `/m/:urlToken/:sessionId`        : URL から現セッション自動選択付き AppHome (user 第 6 弾、L2)
 *   - `/m/:urlToken/:sessionId/map`    : URL から現セッション自動選択付き MapPage (user 第 8 弾、L2)
 *   - その他                           : `/` にリダイレクト
 *
 * 地区フォールバック根絶 (2026-06-08): 旧 provider 外ルート (`/_legacy/home` `/post` `/list` `/map`
 * `/analysis` `/map-download`) は地区未解決 = phase2-default fallback でしか動かず、誤地区への
 * 読み書きの温床だったため撤去。地区依存ページは `/m/:urlToken/...` (DistrictProvider 配下) のみ。
 * urlToken 無しの直アクセスは `*` catch-all で `/` (参加地区一覧) にリダイレクトする。
 * orphan PWA のデバイス引き継ぎ受領フローは DistrictHomePage (`/`) が担当 (機能喪失なし)。
 *
 * 注: `/map-download` / `/analysis` 配下は `:sessionId` deep route 未対応 (UX 要件次第で
 * 追加。現状これらの page では session を直接参照していないため後回し、docs/97 §3 user 第 8 弾)。
 *
 * recovery/onboarding/verification 配下 routes は未実装 (Phase 5 sprint で各 feature 移植時に追加、
 * docs/97 §3 移植ログ参照)。
 */

/**
 * 地区「強い停止」Phase 1: 停止中 (一般ユーザー向け suspended:true 最小返却) は、`/m/:urlToken` 配下の
 * **どのルートでも**「現在非公開中」画面に短絡する。これにより map / post / list / index 等を開いていた
 * 一般ユーザーが更新しても、地区依存ページが null 地区で無限ローディングに陥らない。ただし関係者ログイン
 * (account-login) だけは通し、ログイン後に完全返却で再解決して通常フローへ戻れるようにする。
 */
function DistrictGate() {
  const state = useDistrictState();
  const { accessDenied } = useDistrictAccessGuard();
  const { urlToken } = useParams();
  const location = useLocation();
  const onAccountLogin = location.pathname.endsWith('/account-login');
  // 停止 (resolveDistrictByToken が suspended:true 返却) または、キャッシュ済み一般ユーザーが投稿等で
  // permission-denied を受けた (accessDenied) 場合に「現在非公開中」へ短絡する。後者は古いキャッシュで
  // アプリが描画されたまま投稿だけ読めない状態の救済 (resolveDistrictByToken の再解決を待たずに切替)。
  // 関係者ログイン (account-login) だけは通し、ログイン後に完全返却で再解決して通常フローへ戻れるようにする。
  const suspended = state?.status === 'ready' && state.district.suspended === true;
  if ((suspended || accessDenied) && !onAccountLogin) {
    const districtName = state?.status === 'ready' ? state.district.districtName : undefined;
    const nonPublicMessage =
      state?.status === 'ready' ? state.district.nonPublicMessage : undefined;
    return (
      <NonPublicScreen
        districtName={districtName}
        nonPublicMessage={nonPublicMessage}
        loginHref={`/m/${urlToken}/account-login`}
      />
    );
  }
  return <Outlet />;
}

function DistrictLayout() {
  return (
    <DistrictProvider>
      <DistrictGate />
    </DistrictProvider>
  );
}

export function App() {
  // Sprint BG-DataSettings 17: 全 Routes を PageErrorBoundary で包む (旧 v1 踏襲、
  // U-C32)。例外を Sentry stub に転送 (DSN 未設定時は silent no-op、Sprint BG Part 2)。
  return (
    <BrowserRouter>
      <PageErrorBoundary onError={(err) => captureException(err)}>
        <Routes>
          {/* Sprint BG-DataSettings 9 (GAP U-S1): `/` は参加地区一覧 (DistrictHomePage)。
              地区フォールバック根絶 (2026-06-08): 旧 provider 外の地区ページルート
              (`/_legacy/home` `/post` `/list` `/map` `/analysis` `/map-download`) は phase2-default
              fallback でしか動かないため撤去。地区依存ページは `/m/:urlToken/...` のみ。 */}
          <Route path="/" element={<DistrictHomePage />} />
          {/* Sprint BG-DataSettings 17: 使い方ガイド (U-C33)。地区非依存なので Provider 外で可。 */}
          <Route path="/guide" element={<GuidePage />} />
          {/* 短縮アドレス (docs/26): `/s/:code` → resolveShortLink で「今の urlToken」を引き当て、
              `/m/:urlToken` (セッション指定つきなら参加 QR と同じ `/m/:urlToken/join/:sessionId`) へ
              置換遷移する。地区未解決の中継点なので DistrictProvider の外に置く。 */}
          <Route path="/s/:code" element={<ShortLinkPage />} />
          <Route path="/m/:urlToken" element={<DistrictLayout />}>
            {/* old2 入口フロー移植 (docs/105 §9): index を EntryPage (U0 初参加 / 復帰選択) に置換。
                EntryPage が Firestore membership (consentAcceptedAt && nickname) で onboarding 済を
                判定し、未参加 → onboarding、参加コード未発行 → recovery/issue、完了 → map に振り分ける。 */}
            <Route index element={<EntryPage />} />
            {/* old2 OnboardingPage 移植: プロフィール → 同意モーダル → 権限取得モーダル → createMembership。 */}
            <Route path="onboarding" element={<OnboardingPage />} />
            {/* セッションごとの参加 QR (2026-09-04)。管理画面が発行する QR の遷移先。
                参加予定のセッションを sessionStorage に保存してから deep route へ送るため、
                未参加者がユーザー設定を経由してもセッションの選択が失われない (旧2 OldUrlRedirect 踏襲)。
                ユーザー設定 (RequireOnboarding) の外に置くのは、未参加者もこの URL を踏むため。 */}
            <Route path="join/:sessionId" element={<SessionJoinPage />} />
            {/* 地区「強い停止」Phase 1: 停止中地区で一般ユーザーに出る NonPublicScreen からの
                関係者ログイン導線。email/password ログイン後、権限があれば通常フローへ戻す。 */}
            <Route path="account-login" element={<AccountLoginPage />} />
            {/* old2 RecoveryIssuePage 移植: 参加コード発行 (issueRecoveryCode callable + QR)。 */}
            <Route path="recovery/issue" element={<RecoveryIssuePage />} />
            {/* old2 RecoveryRecoverPage 移植 (暫定): RecoveryRecoverModal を全画面ラップ。 */}
            <Route path="recovery/recover" element={<RecoveryRecoverPage />} />
            {/* Sprint BJ Part 1: QR ディープリンク自動復帰 (旧 RecoveryAutoPage 移植)。
                DistrictProvider 配下に置くと urlToken 未解決時に loading で詰まる risk があるため、
                providersには参加させず Outlet 直下に置くが、AppHome / MapPage と階層を揃え URL 構造を一致させる。 */}
            <Route path="recovery/auto" element={<RecoveryAutoPage />} />
            {/* Sprint BG-DataSettings 17: 使い方ガイド (Provider 配下版、戻る link が地区 home へ)。
                ユーザー設定不要なので guard の外。 */}
            <Route path="guide" element={<GuidePage />} />
            {/* ユーザー設定(オンボーディング)必須の本体ルート。未設定の一般ユーザーは RequireOnboarding が
                入口 (/m/:urlToken、EntryPage) へ強制送還し、新規参加 or 復帰コード入力/発行へ振り分ける。
                関係者(master/担当地区管理者)はロールで閲覧可のため対象外 (docs/112 §3-2)。 */}
            <Route element={<RequireOnboarding />}>
              <Route path="map" element={<MapPage />} />
              {/* Sprint AA: 下部タブ駆動の `/post` `/list` deep route (旧 MainPage の TabBar pattern)。 */}
              <Route path="post" element={<AppHome />} />
              <Route path="list" element={<AppHome />} />
              <Route path="map-download" element={<MapDownloadPage />} />
              <Route path="analysis" element={<AnalysisPage />} />
              {/* user 第 6 弾: 旧 `/m/:districtToken/:sessionId` 互換。
                  AppHome / MapPage 内部で useApplyUrlSessionId() が URL の sessionId を読み取り、
                  active session 一覧に存在するときに currentSession に自動適用する。
                  user 第 8 弾: `/map` deep route 対応。Sprint AA: 同 deep session 経路にも `/post` `/list` を追加。 */}
              <Route path=":sessionId">
                {/* Sprint BG-DataSettings 26: session index も map にリダイレクト。 */}
                <Route index element={<Navigate to="map" replace />} />
                <Route path="map" element={<MapPage />} />
                <Route path="post" element={<AppHome />} />
                <Route path="list" element={<AppHome />} />
              </Route>
            </Route>
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        {/* PWA 任意更新バナー (prompt 方式、2026-06-12)。全ルート共通で画面下部に fixed 表示。 */}
        <UpdateBanner />
      </PageErrorBoundary>
    </BrowserRouter>
  );
}
